import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { useContactService } from 'hooks/api/users/contact';
import { formatDateForDisplay } from 'app/shared/utils/date';
import { useMobilePushLogService } from 'hooks/messaging/mobile/log';
import { Badge } from 'app/shared/badge';

export const WebPushMessageLogs = ({ id: message_id }) => {
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const { fetchContactsBulk, searchContact } = useContactService();
  const { fetchLogs } = useMobilePushLogService();

  const [logs, setLogs] = useState({});
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total_logs, setTotalLogs] = useState(0);

  const config = {
    actions: { single: () => [] },
    css: {},
    fields: [
      {
        title: 'First name',
        key: 'recipient',
        formatter: (value) => (value ? value.firstname : 'N/A'),
        isTitle: true
      },
      {
        title: 'Last name',
        key: 'recipient',
        formatter: (value) => (value ? value.lastname : 'N/A'),
        isTitle: true
      },
      {
        title: 'Email address',
        key: 'recipient',
        isTagline: true,
        formatter: (value) => value?.email || 'N/A'
      },
      {
        title: 'Time sent',
        key: 'time_stamp',
        isMetadata: true,
        formatter: (value) => formatDateForDisplay(value)
      },
      {
        title: 'Status',
        key: 'status',
        formatter: (value) => <Badge type={value === 'sent' ? 'green' : 'red'} text={value} />
      }
    ],
    is_search_mode,
    items: Object.values(logs).sort((a, b) => b.time_stamp - a.time_stamp),
    search_key: 'name',
    search_text: ''
  };

  const handleDataRequest = async (page, population = 10) => {
    if (loading) return;
    setLoading(true);

    const { logs: message_logs, size } = await fetchLogs({
      query_string: `resource_id=${message_id}&page=${page}&population=${population}&sort_by=-created_on`
    });

    const contact_emails = message_logs.map((action) => action.recipient_email);
    const { contacts } = await fetchContactsBulk({
      data: {
        email: contact_emails.join(','),
        tenant_id: message_logs[0]?.tenant_id
      }
    });
    const contacts_by_email = contacts.reduce((s, c) => ({ ...s, [c.email]: c }), {});

    const logs = message_logs.map((log) => ({
      ...log,
      recipient: contacts_by_email[log.recipient_email]
    }));

    setLogs(() => logs.reduce((s, a) => ({ ...s, [a.time_stamp]: a }), {}));
    setTotalLogs(() => size);

    setLoading(false);
  };

  const handleSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys || !keyword) {
      setSearchMode(false);
      await handleDataRequest(page, population);
      return;
    }

    setLoading(true);
    try {
      const { contacts } = await searchContact(keys, keyword, {
        query_string: `page=${page}&population=${population}&resource_id=${message_id}`
      });

      if (contacts.length) {
        const contacts_by_id = contacts.reduce((s, c) => ({ ...s, [c.id]: c }), {});
        const contact_ids = contacts.map((c) => c.id);
        const { maillogs, size } = await fetchLogs({
          query_string: `resource_id=${message_id}&recipient_id=${contact_ids.join()}&page=${page}&population=${population}&sort_by=-created_on`
        });

        const logs = maillogs.map((log) => ({
          ...log,
          recipient: contacts_by_id[log.recipient_id]
        }));

        setLogs(() => logs.reduce((s, a) => ({ ...s, [a.time_stamp]: a }), {}));
        setSearchMode(true);
        setTotalLogs(() => size);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <GridRow>
      <GridColumn span={4}>
        {is_mobile_view ? (
          <MobileDatatable
            config={config}
            action={() => {}}
            onClick={() => {}}
            onListModeChange={setSearchMode}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
            showHeader
          />
        ) : (
          <WebDatatable
            config={{
              ...config,
              total_count: total_logs
            }}
            action={() => {}}
            onClick={() => {}}
            checkbox
            loading_data={loading}
            table_actions={[]}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
        )}
      </GridColumn>
    </GridRow>
  );
};
